import { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  makeStyles,
  Typography,
} from '@material-ui/core';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';

const useStyles = makeStyles(theme => ({
  listItem: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
  },
  personalItem: {
    backgroundColor: theme.palette.action.selected,
  },
  hint: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
}));

interface ReorderDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (order: string[]) => Promise<void>;
  groups: string[];
  userRef?: string;
  currentOrder?: string[];
}

export const ReorderDialog = ({
  open,
  onClose,
  onSave,
  groups,
  userRef,
  currentOrder,
}: ReorderDialogProps) => {
  const classes = useStyles();

  // Initialize order: if currentOrder exists use it, otherwise default order
  // Personal (userRef) is always conceptually first but stored in the order
  const getInitialOrder = useCallback(() => {
    const allRefs = userRef ? [userRef, ...groups] : groups;
    if (currentOrder && currentOrder.length > 0) {
      // Use currentOrder but ensure all current groups are included
      const orderedSet = new Set(currentOrder);
      const result = currentOrder.filter(ref => allRefs.includes(ref));
      // Add any missing refs at the end
      allRefs.forEach(ref => {
        if (!orderedSet.has(ref)) {
          result.push(ref);
        }
      });
      return result;
    }
    return allRefs;
  }, [groups, userRef, currentOrder]);

  const [order, setOrder] = useState<string[]>(getInitialOrder);
  const [saving, setSaving] = useState(false);

  const handleMoveUp = useCallback((index: number) => {
    if (index === 0) return;
    setOrder(prev => {
      const newOrder = [...prev];
      [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
      return newOrder;
    });
  }, []);

  const handleMoveDown = useCallback((index: number) => {
    setOrder(prev => {
      if (index === prev.length - 1) return prev;
      const newOrder = [...prev];
      [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
      return newOrder;
    });
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await onSave(order);
      onClose();
    } catch (err) {
      console.error('Failed to save order:', err);
    } finally {
      setSaving(false);
    }
  }, [order, onSave, onClose]);

  const formatGroupName = (groupRef: string) => {
    if (groupRef.startsWith('user:')) {
      return 'Myself (Personal)';
    }
    const parts = groupRef.split('/');
    const name = parts[parts.length - 1];
    return name.charAt(0).toUpperCase() + name.slice(1).replace(/-/g, ' ');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Reorder Groups</DialogTitle>
      <DialogContent>
        <Typography variant="body2" className={classes.hint}>
          Change the order in which links from different groups appear. Links from groups higher in the list will appear first.
        </Typography>
        <List>
          {order.map((ref, index) => (
            <ListItem
              key={ref}
              className={`${classes.listItem} ${ref === userRef ? classes.personalItem : ''}`}
            >
              <ListItemText
                primary={formatGroupName(ref)}
                secondary={ref === userRef ? 'Your personal links' : undefined}
              />
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  size="small"
                  onClick={() => handleMoveUp(index)}
                  disabled={index === 0}
                >
                  <ArrowUpwardIcon />
                </IconButton>
                <IconButton
                  edge="end"
                  size="small"
                  onClick={() => handleMoveDown(index)}
                  disabled={index === order.length - 1}
                >
                  <ArrowDownwardIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Order'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
